import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  InlineLoading,
  Toggle,
  Checkbox,
  Dropdown,
  ToastNotification,
} from "@carbon/react";

const Amenities = ({
  data = {},
  propertyUuid,
  attributeValues = [],
  isEditMode = false,
  currentStep = 4,
  totalSteps = 6,
  onBack,
  onNavigateNext,
  onSaveSuccess,
  createProperty,
  saveSectionAttributes,
  CommonButtons,
  contactData,
  formMode = "add",
  lastSavedStep = 0
}) => {

  console.log('🔍 Amenities (Step 4) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditForm: formMode === "edit" || lastSavedStep >= currentStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    hasAmenities: !!data?.amenities,
    amenitiesData: data?.amenities
  });


  // EDIT MODE CALCULATION
  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  // FIELD MAP 
  const FIELD_MAP = useMemo(() => ({
    attachedBathroom: "flat_attached_bathroom",
    acRoom: "flat_ac_room", 
    balcony: "flat_balcony",
    nonVegAllowed: "flat_non_veg_allowed",
    smokingAllowed: "flat_smoking_allowed", 
    drinkingAllowed: "flat_drinking_allowed",
    gym: "flat_gym",
    gatedSecurity: "flat_gated_security",
    waterSupply: "flat_water_supply",
    directions: "flat_direction_tip",
    selectedAmenities: "flat_amenities"
  }), []);

  // STATE
  const [form, setForm] = useState({
    attachedBathroom: false,
    acRoom: false,
    balcony: false,
    nonVegAllowed: false,
    smokingAllowed: false,
    drinkingAllowed: false,
    gym: false,
    gatedSecurity: false,
    waterSupply: "",
    selectedAmenities: []
  });

  const [, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");


  // API OPTIONS
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );
  
  const requiredLabel = (text) => (
  <>
    {text} <span style={{ color: "#da1e28" }}>*</span>
  </>
);

  // Get amenities list from attribute values
  const amenitiesList = useMemo(() => {
    return getOptions("selectedAmenities");
  }, [getOptions]);

  // FIELD UPDATES
  const updateField = useCallback((key, value) => {
    console.log(`🔄 Updating field ${key} to:`, value);
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  // LOAD INITIAL DATA 
  useEffect(() => {
    console.log('💧 Amenities hydration triggered with data:', data);

    // Check if we have data to hydrate from
    if (!data || Object.keys(data).length === 0) {
      console.log('💧 No data available for hydration');
      return;
    }

    // Check both possible locations for amenities data
    const amenitiesData = data.amenities || {};
    
    console.log('💧 Checking amenities data:', amenitiesData);

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = amenitiesData[apiKey];
      
      if (raw == null) {
        console.log(`  - ${field} (${apiKey}): No data found in amenities`);
        return;
      }

      console.log(`  - ${field} (${apiKey}): raw value =`, raw, 'type:', typeof raw);

      // Handle boolean fields (toggle fields)
      if (field === "attachedBathroom" || field === "acRoom" || field === "balcony" ||
          field === "nonVegAllowed" || field === "smokingAllowed" || field === "drinkingAllowed" ||
          field === "gym" || field === "gatedSecurity") {
        incoming[field] = raw === true || raw === "true" || raw === 1 || raw === "1" || raw === "Yes";
      } 
      // Handle array field (amenities)
else if (field === "selectedAmenities") {
  const options = getOptions("selectedAmenities");

  if (Array.isArray(raw)) {
    incoming[field] = raw
      .map((value) => {
        const matchedOption = options.find(
          (option) =>
            String(option.id) === String(value) ||
            String(option.label).trim().toLowerCase() ===
              String(value).trim().toLowerCase()
        );

        return matchedOption ? String(matchedOption.id) : null;
      })
      .filter(Boolean);
  } else if (typeof raw === "string") {
    incoming[field] = raw
      .split(",")
      .map((value) => {
        const matchedOption = options.find(
          (option) =>
            String(option.id) === String(value).trim() ||
            String(option.label).trim().toLowerCase() ===
              String(value).trim().toLowerCase()
        );

        return matchedOption ? String(matchedOption.id) : null;
      })
      .filter(Boolean);
  } else {
    incoming[field] = [];
  }
}
      // Handle all other fields
      else {
        incoming[field] = String(raw);
      }
    });

    console.log('💧 Amenities incoming form data:', incoming);
    
    // Update form state with hydrated data
    setForm((prev) => {
      const newForm = { ...prev, ...incoming };
      console.log('💧 Amenities new form state:', newForm);
      return newForm;
    });
  }, [data, FIELD_MAP, getOptions]);

  // CHECKBOX HANDLERS
  const handleAmenityChange = useCallback((amenityId, isChecked) => {
    setForm(prev => {
      const selectedAmenities = [...prev.selectedAmenities];
      const amenityIdStr = String(amenityId);
      
      if (isChecked) {
        if (!selectedAmenities.includes(amenityIdStr)) {
          selectedAmenities.push(amenityIdStr);
        }
      } else {
        const index = selectedAmenities.indexOf(amenityIdStr);
        if (index > -1) {
          selectedAmenities.splice(index, 1);
        }
      }
      return { ...prev, selectedAmenities };
    });
  }, []);

  const isAmenitySelected = useCallback((amenityId) => {
    return form.selectedAmenities.includes(String(amenityId));
  }, [form.selectedAmenities]);

 // SAVE
const handleSave = useCallback(async (navigateNext = false) => {
  console.log("💾 Amenities handleSave:", {
    navigateNext,
    isEditForm,
    currentStep,
    hasPropertyUuid: !!propertyUuid,
    formData: form,
  });

  try {
    setSaving(true);
    setError("");
    setSuccess("");

    let uuid = propertyUuid;

    // Create property if it doesn't exist
    if (!uuid && createProperty) {
      console.log("🆕 Creating property from Step 4...");
      uuid = await createProperty();
      onSaveSuccess?.(uuid, form, currentStep);
    }

    const payload = {};

    for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
      const v = form[field];

      // Skip empty values
      if (
        v == null ||
        v === "" ||
        (Array.isArray(v) && v.length === 0)
      ) {
        continue;
      }

      // Handle boolean fields
      if (
        field === "attachedBathroom" ||
        field === "acRoom" ||
        field === "balcony" ||
        field === "nonVegAllowed" ||
        field === "smokingAllowed" ||
        field === "drinkingAllowed" ||
        field === "gym" ||
        field === "gatedSecurity"
      ) {
        payload[apiKey] = v ? "Yes" : "No";
      }

      // Handle amenities array
      else if (field === "selectedAmenities") {
        if (Array.isArray(v) && v.length > 0) {
          payload[apiKey] = v
            .map((item) => {
              const amenity = amenitiesList.find(
                (a) => String(a.id) === String(item)
              );

              return amenity?.label || "";
            })
            .filter(Boolean);
        }
      }

      // Handle other fields
      else {
        payload[apiKey] = v;
      }
    }

    console.log(
      "🔥 FINAL PAYLOAD FROM AMENITIES:",
      JSON.stringify(payload, null, 2)
    );

    await saveSectionAttributes(
      uuid,
      payload,
      form,
      "amenities"
    );

    setSuccess("Amenities saved successfully!");

    // Notify parent
    onSaveSuccess?.(uuid, form, currentStep);

    // Navigate to next step
    if (navigateNext) {
      console.log("➡️ Navigating to next step after save");
      onNavigateNext?.(form);
    }
  } catch (err) {
    console.error("❌ Save failed:", err);
    setError(err.message || "Failed to save amenities");
  } finally {
    setSaving(false);
  }
}, [
  form,
  isEditForm,
  currentStep,
  propertyUuid,
  createProperty,
  onSaveSuccess,
  FIELD_MAP,
  saveSectionAttributes,
  onNavigateNext,
  amenitiesList,
]);
  // BUTTON HANDLERS
  const handleSaveOnly = useCallback((e) => {
    e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
    handleSave(true);
  }, [handleSave]);

  // RENDER HELPERS
  const renderToggle = (fieldKey, label, required = false) => {
    return (
      <FormGroup legendText={required ? requiredLabel(label) : label}>
        <Toggle
          id={fieldKey}
          labelA="No"
          labelB="Yes"
          toggled={form[fieldKey]}
          onToggle={(checked) => updateField(fieldKey, checked)}
        />
      </FormGroup>
    );
  };

  const renderDropdown = (fieldKey, label, required = false) => {
    const options = getOptions(fieldKey);
    const selectedItem = options.find(opt => String(opt.id) === String(form[fieldKey]));
    
    return (
      <Dropdown
        id={fieldKey}
         titleText={required ? requiredLabel(label) : label}
        label={`Select ${label.toLowerCase()}`}
        items={options}
        itemToString={(item) => item?.label || ""}
        selectedItem={selectedItem}
        onChange={({ selectedItem }) => updateField(fieldKey, selectedItem?.id || "")}
      />
    );
  };

  // LOADING STATE
  if (attributeValues.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        backgroundColor: 'white'
      }}>
        <InlineLoading description="Loading amenities..." />
      </div>
    );
  }

  // -----------------------------
  // MAIN RENDER
  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {/* Error and Success Notifications */}
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid condensed style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        <Column sm={4} md={8} lg={12}>
          <Tile style={{ backgroundColor: 'white' }}>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSaveAndNext(e);
            }}>
              <Stack gap={6}>
               
                {/* ROOM DETAILS */}
                <FormGroup>
                  <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Room Details</h4>
                  <Grid>
                    <Column sm={4} md={4} lg={4}>
                      {renderToggle("attachedBathroom", "Attached Bathroom", true)}
                    </Column>
                    <Column sm={4} md={4} lg={4}>
                      {renderToggle("acRoom", "AC Room", false)}
                    </Column>
                    <Column sm={4} md={4} lg={4}>
                      {renderToggle("balcony", "Balcony", false)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* FLATMATE PREFERENCE */}
                <FormGroup>
                  <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Flatmate Preference</h4>
                  <Grid>
                    <Column sm={4} md={4} lg={4}>
                      {renderToggle("nonVegAllowed", "Non-Veg Allowed", false)}
                    </Column>
                    <Column sm={4} md={4} lg={4}>
                      {renderToggle("smokingAllowed", "Smoking Allowed", false)}
                    </Column>
                    <Column sm={4} md={4} lg={4}>
                      {renderToggle("drinkingAllowed", "Drinking Allowed", false)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* ADDITIONAL DETAILS */}
                <FormGroup>
                  <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Additional Details for maximum visibility</h4>
                  <Grid>
                    <Column sm={4} md={4} lg={4}>
                      {renderToggle("gym", "Gym", false)}
                    </Column>
                    <Column sm={4} md={4} lg={4}>
                      {renderToggle("gatedSecurity", "Gated Security", false)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* WATER SUPPLY */}
                <FormGroup>
                  <Grid>
                    <Column sm={4} md={4} lg={8}>
                      {renderDropdown("waterSupply", "Water Supply", false)}
                    </Column>
                  </Grid>
                </FormGroup>

                {/* AMENITIES GRID */}
                {amenitiesList.length > 0 && (
                  <FormGroup>
                    <h4 style={{ marginBottom: '1rem', fontWeight: '600' }}>Available Amenities</h4>
                    <Grid>
                      {amenitiesList.map((amenity) => (
                        <Column key={amenity.id} sm={4} md={4} lg={4}>
                          <Checkbox
                            id={`amenity-${amenity.id}`}
                            labelText={amenity.label}
                            checked={isAmenitySelected(amenity.id)}
                            onChange={(_, { checked }) => handleAmenityChange(amenity.id, checked)}
                          />
                        </Column>
                      ))}
                    </Grid>
                  </FormGroup>
                )}

                {/* Use CommonButtons from parent for consistent navigation */}
                {CommonButtons && (
                  <CommonButtons
                    onSave={handleSaveOnly}
                    onNext={handleSaveAndNext}
                    showSave
                    showNext
                    isSaveLoading={saving}
                    stepData={form}
                  />
                )}

               
              </Stack>
            </form>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default Amenities;